import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../AuthContext";

const APropos = () => {
  const { isAuthenticated } = useAuth();
  const [isContactPromptOpen, setIsContactPromptOpen] = useState(false);

  const handleContactClick = (e) => {
    if (!isAuthenticated) {
      e.preventDefault();
      setIsContactPromptOpen(true);
    }
  };

  return (
    <>
      <div className="bg-gray-900 text-white min-h-screen py-16">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl">
          <section className="text-center mb-16">

            <h1 className="text-4xl md:text-6xl text-orange-500 font-extrabold leading-tight mb-6">
              À propos
            </h1>

            <p className="max-w-3xl mx-auto text-lg md:text-xl text-gray-300 leading-relaxed">
              La photographie est pour moi une manière de figer l’instant,
              d’exprimer une émotion et de raconter une histoire à travers la
              lumière, les couleurs et les regards.
            </p>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-20">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500/20 blur-3xl rounded-full" />
                <img
                  src={`${process.env.REACT_APP_IMG_URL}/storage/images/front/geo.png`}
                  alt="Portrait de Schroeders Geoffrey"
                  className="relative w-72 md:w-96 h-auto rounded-3xl shadow-2xl border border-gray-700 object-cover"
                />
              </div>
            </div>

            <div className="bg-gray-800/70 border border-gray-700 rounded-2xl p-8 shadow-xl">
              <h2 className="text-3xl font-bold mb-4 text-orange-500">
                Ma vision
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                À travers mes images, je cherche à révéler ce qui passe parfois
                inaperçu : une lumière particulière, une émotion discrète, une
                ambiance, un détail qui transforme un moment ordinaire en souvenir
                marquant.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                Mon approche repose sur la sincérité, l’observation et le plaisir
                de créer des images qui ont du sens, à la fois esthétiques et
                vivantes.
              </p>
            </div>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition duration-300">
              <h2 className="text-2xl font-bold mb-4 text-orange-500">
                Ma passion
              </h2>
              <p className="text-gray-300 leading-relaxed">
                Depuis toujours, la photographie représente bien plus qu’un simple
                loisir. C’est un moyen d’expression artistique qui me permet de
                capturer des instants précieux et de transmettre des émotions
                authentiques.
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition duration-300">
              <h2 className="text-2xl font-bold mb-4 text-orange-500">
                Mon parcours
              </h2>
              <p className="text-gray-300 leading-relaxed">
                Même si la photographie n’est pas mon activité principale, elle
                occupe une place essentielle dans ma vie. Mes expériences, mes
                voyages, mes rencontres et les scènes du quotidien nourrissent mon
                regard et enrichissent ma sensibilité visuelle.
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition duration-300">
              <h2 className="text-2xl font-bold mb-4 text-orange-500">
                Mon univers
              </h2>
              <p className="text-gray-300 leading-relaxed">
                J’aime chercher la beauté dans l’ordinaire : un paysage, une rue,
                une atmosphère, un visage, une lumière. Chaque image devient une
                invitation à ralentir, observer et ressentir.
              </p>
            </div>
          </section>

          <section className="bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-3xl p-10 md:p-14 text-center shadow-xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Merci de visiter mon univers
            </h2>

            <p className="max-w-3xl mx-auto text-lg text-gray-300 leading-relaxed mb-8">
              J’espère que mes images sauront vous toucher, éveiller votre
              curiosité ou simplement vous offrir un instant de contemplation.
              N’hésitez pas à me contacter pour échanger, partager vos impressions
              ou discuter d’un projet.
            </p>

            <Link
              to="/contact"
              onClick={handleContactClick}
              className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-full transition duration-300 shadow-lg"
            >
              Me contacter
            </Link>
          </section>
        </div>
      </div>

      {isContactPromptOpen && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setIsContactPromptOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-[#111] text-white p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold mb-3 text-center">
              Accès au contact
            </h2>

            <p className="text-gray-300 text-center mb-6 leading-relaxed">
              Pour accéder au formulaire de contact, vous devez être connecté à
              votre compte.
            </p>

            <div className="flex flex-col gap-3">
              <Link
                to="/login"
                className="w-full text-center px-6 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 transition font-semibold"
                onClick={() => setIsContactPromptOpen(false)}
              >
                Se connecter
              </Link>

              <Link
                to="/register"
                className="w-full text-center px-6 py-3 rounded-xl border border-white/15 hover:bg-white/10 transition font-semibold"
                onClick={() => setIsContactPromptOpen(false)}
              >
                Créer un compte
              </Link>

              <button
                type="button"
                onClick={() => setIsContactPromptOpen(false)}
                className="w-full text-center px-6 py-3 rounded-xl text-gray-400 hover:text-white transition"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default APropos;